import { TooltipTouchContext, useTooltipTouchInput } from './tooltipInput';
import { Tooltip, type TooltipProps } from '@blueprintjs/core';
import {
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  useEffect,
  useId,
  useState,
  type AriaAttributes,
  type ReactElement,
} from 'react';

type TargetProps = Parameters<NonNullable<TooltipProps['renderTarget']>>[0] &
  Pick<AriaAttributes, 'aria-describedby'>;
type AppTooltipProps = Omit<TooltipProps, 'renderTarget'> & {
  renderTarget?: (props: TargetProps) => ReactElement;
};

// Blueprint's hover interaction also injects aria-haspopup="menu" into its
// child target. A tooltip is descriptive, not a menu. Keep the original child's
// semantics and ref while retaining Blueprint's generated keyboard tab stop.
function TooltipChild({
  children,
  tabIndex,
}: {
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  tabIndex?: number;
}) {
  return cloneElement(children, { tabIndex: children.props.tabIndex ?? tabIndex });
}

/** Shared hints; static targets opt out of focus, controls retain their own names. */
export function AppTooltip({
  content,
  children,
  renderTarget,
  onInteraction,
  ...props
}: AppTooltipProps) {
  const touchInput = useTooltipTouchInput();
  const id = useId();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (props.disabled) {
      setOpen(false);
      return;
    }
    if (!open) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Retain focus without Blueprint returning it and reopening a focused hint.
      // Consume this Escape before the enclosing dialog/menu closes.
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('keydown', dismiss, true);
    return () => document.removeEventListener('keydown', dismiss, true);
  }, [open, props.disabled]);
  return (
    <Tooltip
      placement="top"
      hoverOpenDelay={300}
      hoverCloseDelay={100}
      interactionKind="hover"
      usePortal
      canEscapeKeyClose={false}
      captureDismiss
      {...props}
      isOpen={open && !props.disabled}
      onInteraction={(next, event) => {
        setOpen(next);
        onInteraction?.(next, event);
      }}
      content={
        <span role="tooltip" id={id}>
          <TooltipTouchContext.Provider value={touchInput}>
            {content}
          </TooltipTouchContext.Provider>
        </span>
      }
      renderTarget={
        renderTarget
          ? (target) =>
              renderTarget({
                ...target,
                'aria-haspopup': undefined,
                'aria-describedby': target.isOpen ? id : undefined,
              })
          : undefined
      }
    >
      {renderTarget ? undefined : (
        <TooltipChild>
          {isValidElement<HTMLAttributes<HTMLElement>>(children) ? (
            children
          ) : (
            <span>{children}</span>
          )}
        </TooltipChild>
      )}
    </Tooltip>
  );
}
